import { createContext, useContext, useEffect, useState } from "react";
import { api, authStore } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => authStore.getUser());
  const [booting, setBooting] = useState(true);

  // On mount: if we have a stored token, confirm it with /auth/me.
  useEffect(() => {
    const token = authStore.getToken();
    if (!token) {
      setBooting(false);
      return;
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        authStore.save(token, data);
      })
      .catch(() => {
        authStore.clear();
        setUser(null);
      })
      .finally(() => setBooting(false));
  }, []);

  // Global 401 listener (fired by apiClient's response interceptor)
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener("po:auth-expired", onExpired);
    return () => window.removeEventListener("po:auth-expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post(
      "/auth/login",
      { email, password },
      { _skip401Logout: true },
    );
    authStore.save(data.token, data.user);
    setUser(data.user);
    return data.user;
  };

  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    authStore.save(data.token, data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    authStore.clear();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      const token = authStore.getToken();
      if (token) authStore.save(token, data);
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, booting, login, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
